import { forwardRef, useEffect, useRef } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import { tv } from 'tailwind-variants'

import { Icon } from '@/components/atoms/Icon/Icon'

export type TCheckboxSize = 'sm' | 'md' | 'lg'

export type TCheckboxProps = {
  label?: string
  checked?: boolean
  partial?: boolean
  size?: TCheckboxSize
} & Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'onBlur' | 'disabled' | 'onChange' | 'checked'
>

const checkboxVariants = tv({
  slots: {
    labelSlot: 'flex items-center gap-2',
    containerSlot: 'relative',
    checkboxSlot:
      'focus:ring-surface-black-50 flex flex-shrink-0 items-center justify-center rounded border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
    textSlot: ''
  },
  variants: {
    size: {
      sm: {
        checkboxSlot: 'h-3 w-3',
        textSlot: 'text-caption-lg'
      },
      md: {
        checkboxSlot: 'h-4 w-4',
        textSlot: 'text-body-md'
      },
      lg: {
        checkboxSlot: '4xl:h-[21px] 4xl:w-[21px] h-[18px] w-[18px]',
        textSlot: 'text-body-xl whitespace-nowrap font-normal'
      }
    },
    state: {
      checked: {
        checkboxSlot: 'bg-surface-title-50 border-surface-title-50',
        textSlot: 'text-surface-title-50 font-bold'
      },
      partial: {
        checkboxSlot: 'bg-surface-title-50 border-surface-title-50',
        textSlot: 'text-surface-title-50 font-bold'
      },
      unchecked: {
        checkboxSlot: 'bg-surface-white-50 border-surface-title-50',
        textSlot: 'text-surface-title-50 font-medium'
      }
    },
    disabled: {
      true: { labelSlot: 'cursor-not-allowed' },
      false: { labelSlot: 'cursor-pointer' }
    }
  },
  compoundVariants: [
    {
      state: ['checked', 'partial'],
      disabled: true,
      class: {
        checkboxSlot: 'bg-surface-white-50 border-surface-disabled-text',
        textSlot: 'text-surface-disabled-text'
      }
    },
    {
      state: 'unchecked',
      disabled: true,
      class: {
        checkboxSlot: 'bg-surface-white-50 border-surface-disabled-text',
        textSlot: 'text-surface-disabled-text'
      }
    }
  ],
  defaultVariants: { size: 'md', state: 'unchecked', disabled: false }
})

// Icon sizes chosen to fit within each checkbox box without overflow:
// sm (12px box) → 'xs' (12px icon)
// md (16px box) → 'sm' (14px icon)
// lg (18px box) → 'lg' (18px icon)
const iconSizeMap: Record<
  TCheckboxSize,
  'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
> = {
  sm: 'xs',
  md: 'sm',
  lg: 'lg'
}

const CheckboxIcon = ({
  state,
  size,
  disabled
}: {
  state: 'checked' | 'partial' | 'unchecked'
  size: TCheckboxSize
  disabled?: boolean
}) => {
  const isVisible = state !== 'unchecked'
  const iconName = state === 'partial' ? 'check_indeterminate_small' : 'check'
  const iconColor = disabled ? 'surface-disabled-text' : 'surface-white-50'

  return (
    <AnimatePresence mode='wait'>
      {isVisible && (
        <motion.span
          key={state}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className='flex items-center justify-center'
        >
          <Icon
            name={iconName}
            color={iconColor}
            size={iconSizeMap[size]}
            ariaHidden
          />
        </motion.span>
      )}
    </AnimatePresence>
  )
}

export { checkboxVariants }

/**
 * @deprecated Use `Checkbox` from `@alternative/acqua/molecules` instead. The acqua-ui Checkbox is being phased out in favor of the acqua design system.
 */
export const Checkbox = forwardRef<HTMLInputElement, TCheckboxProps>(
  (
    {
      label,
      checked = false,
      partial = false,
      onChange,
      size = 'md',
      name,
      onBlur,
      disabled = false
    },
    ref
  ) => {
    const checkboxState = partial
      ? 'partial'
      : checked
      ? 'checked'
      : 'unchecked'

    /**
     * `partial` draws a dash, but `checked` stays a boolean — so without this the
     * input announces "not checked" while the box shows a half-selection, and
     * every bulk-select header reads as empty to a screen reader.
     *
     * Set as the native DOM property rather than `aria-checked='mixed'`: on an
     * `input[type=checkbox]` that ARIA value contradicts the element's own state
     * and axe rejects it (`aria-conditional-attr`). `indeterminate` is the
     * supported mechanism, and the browser maps it to "mixed" itself.
     */
    const inputRef = useRef<HTMLInputElement | null>(null)
    useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = partial
    }, [partial])
    const { labelSlot, containerSlot, checkboxSlot, textSlot } =
      checkboxVariants({ size, state: checkboxState, disabled })

    return (
      <div className={containerSlot()}>
        <label className={labelSlot()}>
          <input
            type='checkbox'
            checked={checked}
            onChange={onChange}
            readOnly={!onChange}
            disabled={disabled}
            id={name}
            name={name}
            onBlur={onBlur}
            ref={node => {
              inputRef.current = node
              if (typeof ref === 'function') ref(node)
              else if (ref) ref.current = node
            }}
            className='sr-only'
          />
          <span className={checkboxSlot()}>
            <CheckboxIcon
              state={checkboxState}
              size={size}
              disabled={disabled}
            />
          </span>
          {label && (
            <span id={`${name}-label`} className={textSlot()}>
              {label}
            </span>
          )}
        </label>
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'
